/**
 * ч.1374 — три ознаки ПДВ документа як у 1С («Ціни та валюта» → Податки):
 *   • «Враховувати ПДВ»        (УТП УчитыватьНДС)      — у документі є ПДВ узагалі;
 *   • «Сума включає ПДВ»       (УТП СуммаВключаетНДС)  — ціна рядка вже з ПДВ;
 *   • «Авторозрахунок ПДВ»     (УТП АвторасчетНДС)     — суму ПДВ рядка рахує система,
 *                                                        інакше її можна вписати вручну.
 * Умовчання — з картки організації (`*_default`), документ міняє їх у себе кнопкою «ціна»
 * поруч із «+ додати товар». Одна арифметика рядка для всіх редакторів (замовлення/рахунок,
 * реалізація, надходження, акт) і один спосіб визначити вигляд друкованої форми.
 */

export interface DocVatFlags {
  vat_considered: boolean;
  prices_include_vat: boolean;
  vat_auto: boolean;
}

/** Вигляд цін у документі й на друку: без ПДВ у документі / ціни без ПДВ / ціни з ПДВ. */
export type VatMode = 'none' | 'excluded' | 'included';

/** ч.1375: як в УТП — ціни з ПДВ («СуммаВключаетНДС» = Істина), поки картка не каже інакше. */
export const DEFAULT_VAT_FLAGS: DocVatFlags = {
  vat_considered: true, prices_include_vat: true, vat_auto: true,
};

export interface OrgVatDefaults {
  is_vat_payer: boolean;
  vat_considered_default?: boolean;
  prices_include_vat_default?: boolean;
  vat_auto_default?: boolean;
  /** ч.1383: податковий статус з дати (за зростанням дати). */
  tax_history?: { valid_from: string; is_vat_payer: boolean; tax_system?: string }[];
}

/** ч.1383: податковий статус організації на дату документа (рядок історії, що діє на дату;
 *  раніше за перший рядок — перший; без історії чи дати — картка, тобто сьогодні). */
export function taxStatusOn<T extends { valid_from: string }>(
  history: T[] | null | undefined, date: string | null | undefined,
): T | null {
  if (!history?.length || !date) return null;
  const d = date.slice(0, 10);
  let cur: T = history[0]!;
  for (const row of history) {
    if (row.valid_from <= d) cur = row; else break;
  }
  return cur;
}

/** Платник ПДВ на дату документа (статус міг змінитись: колишній платник — документи з ПДВ). */
export function vatPayerOn(org: OrgVatDefaults | null | undefined, date?: string | null): boolean {
  if (!org) return true;
  return taxStatusOn(org.tax_history, date)?.is_vat_payer ?? org.is_vat_payer;
}

/** Була платником ПДВ хоч колись (меню «ПДВ», колонки ПН у списках — і за минулі періоди). */
export function everVatPayer(org: OrgVatDefaults | null | undefined): boolean {
  return !!org && (org.is_vat_payer || !!org.tax_history?.some((r) => r.is_vat_payer));
}

/** Ознаки нового документа за карткою організації (неплатник на дату документа — без ПДВ). */
export function orgVatDefaults(org: OrgVatDefaults | null | undefined, date?: string | null): DocVatFlags {
  if (!org) return { ...DEFAULT_VAT_FLAGS };
  return {
    vat_considered: vatPayerOn(org, date) && (org.vat_considered_default ?? true),
    prices_include_vat: org.prices_include_vat_default ?? true,
    vat_auto: org.vat_auto_default ?? true,
  };
}

/** ч.1381: вирішує ознака САМОГО документа, а статус організації — лише коли ознаки немає.
 *  Статус змінюється: реалізація 2024 р. з ПДВ у ТОВ, яке тепер не платник, друкується з ПДВ. */
export function vatMode(flags: Partial<DocVatFlags> | null | undefined, isVatPayer: boolean): VatMode {
  const considered = flags?.vat_considered ?? isVatPayer;
  if (!considered) return 'none';
  return flags?.prices_include_vat ? 'included' : 'excluded';
}

/** Підпис кнопки «ціна» у таблиці рядків — видно стан без відкриття вікна. */
export function vatFlagsLabel(flags: DocVatFlags): string {
  if (!flags.vat_considered) return 'ціна: без ПДВ у документі';
  return flags.prices_include_vat ? 'ціна з ПДВ' : 'ціна без ПДВ';
}

export const VAT_FLAG_TEXTS: Record<keyof DocVatFlags, { label: string; hint: string }> = {
  vat_considered: {
    label: 'Враховувати ПДВ',
    hint: 'Якщо увімкнено, у рядках є ставка ПДВ з картки номенклатури і сума ПДВ. '
      + 'Вимкнено — документ без ПДВ: на друку «Ціна» і «Сума», без рядків ПДВ.',
  },
  prices_include_vat: {
    label: 'Сума включає ПДВ',
    hint: 'Ціна рядка вже містить ПДВ: сума без ПДВ виділяється з неї. '
      + 'На друку — «Ціна з ПДВ», «Сума з ПДВ» і «У тому числі ПДВ».',
  },
  vat_auto: {
    label: 'Автоматичний розрахунок ПДВ',
    hint: 'Суму ПДВ рядка рахує система зі ставки. Вимкнено — суму ПДВ можна вписати '
      + 'вручну (наприклад, щоб збігтися з документом постачальника на копійки).',
  },
};

const r2 = (n: number) => (Math.round((n + Number.EPSILON) * 100) / 100).toFixed(2);

export interface LineAmounts {
  amount_without_vat: string;
  vat_amount: string;
  total_amount: string;
  discount_amount: string;
}

/**
 * Суми рядка з ціни, кількості, знижки та ставки за ознаками документа.
 * `manualVat` — сума ПДВ, вписана вручну (лише при вимкненому авторозрахунку).
 */
export function lineAmounts(
  qty: number, price: number, ratePercent: number, flags: DocVatFlags,
  opts: { discountPercent?: number; manualVat?: number | null } = {},
): LineAmounts {
  const gross = (Number(qty) || 0) * (Number(price) || 0);
  const discAmt = gross * ((Number(opts.discountPercent) || 0) / 100);
  const base = gross - discAmt;
  const rate = flags.vat_considered ? (Number(ratePercent) || 0) : 0;
  let awv: number;
  let vat: number;
  if (!flags.vat_considered) {
    awv = base; vat = 0;
  } else if (flags.prices_include_vat) {
    const total = base;
    vat = opts.manualVat != null && !flags.vat_auto ? Number(opts.manualVat) || 0 : total - total / (1 + rate / 100);
    awv = total - vat;
  } else {
    awv = base;
    vat = opts.manualVat != null && !flags.vat_auto ? Number(opts.manualVat) || 0 : awv * rate / 100;
  }
  return {
    amount_without_vat: r2(awv), vat_amount: r2(vat), total_amount: r2(awv + vat),
    discount_amount: r2(discAmt),
  };
}

export interface VatRow {
  quantity?: string | number | null;
  price?: string | number | null;
  discount_percent?: string | number | null;
  vat_rate_id?: string | null;
  vat_amount?: string | number | null;
}

/** Перерахунок усіх рядків після зміни ознак (ціни лишаються, суми — за новими правилами). */
export function recalcRows<T extends VatRow>(
  rows: T[], flags: DocVatFlags, rateOf: (vatRateId: string | null | undefined) => number,
): T[] {
  return rows.map((row) => {
    const a = lineAmounts(Number(row.quantity ?? 0), Number(row.price ?? 0), rateOf(row.vat_rate_id), flags,
      { discountPercent: Number(row.discount_percent ?? 0), manualVat: flags.vat_auto ? null : Number(row.vat_amount ?? 0) });
    return { ...row, amount_without_vat: a.amount_without_vat, vat_amount: a.vat_amount,
      total_amount: a.total_amount, ...('discount_amount' in row ? { discount_amount: a.discount_amount } : {}) };
  });
}

/** Ціна і сума рядка так, як їх треба показати на друку в цьому режимі. */
export function printLineAmount(
  it: { amount_without_vat: string | number; vat_amount: string | number }, mode: VatMode,
): number {
  const awv = Number(it.amount_without_vat) || 0;
  return mode === 'included' ? awv + (Number(it.vat_amount) || 0) : awv;
}
